'use client';

import { useEffect, useRef } from 'react';


const features = [
  {
    id: 'vanilla',
    title: '100% Vanilla',
    description: 'Pure Rust experience. No plugins that change gameplay. Just you, your skills, and the island.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: '#22c55e',
    badges: ['No Kits', 'No TP', 'No /home', 'Facepunch Vanilla'],
  },
  {
    id: 'ping',
    title: 'Low Ping EU',
    description: 'Premium OVH infrastructure in Frankfurt. Average ping under 20ms for EU players.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: '#6a5cff',
    stat: '<20ms',
  },
  {
    id: 'bp-wipes',
    title: 'No BP Wipes',
    description: 'Keep your blueprints forever. Only map wipes on schedule. Your progress matters.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: '#f59e0b',
  },
  {
    id: 'admins',
    title: 'Active Admins',
    description: '24/7 admin coverage across timezones. Fast response to reports. Zero tolerance for cheaters.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: '#6a5cff',
    stats: [
      { label: 'Avg Ban Time', value: '< 2hrs' },
      { label: 'Admins Online', value: 'Daily' },
      { label: 'Response Time', value: '< 30min' },
    ],
  },
  {
    id: 'fair-play',
    title: 'Fair Play',
    description: 'Strict anti-cheat, no pay-to-win perks. Everyone plays by the same rules.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: '#22c55e',
  },
  {
    id: 'community',
    title: 'Community First',
    description: 'Active Discord, community events, and we actually listen to feedback.',
    icon: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: '#8b7fff',
  },
];

const stats = [
  { label: 'Uptime', value: '99.9%' },
  { label: 'Avg Ban Time', value: '<2h' },
  { label: 'Year Running', value: '1+' },
  { label: 'Unique Players', value: '5k+' },
];

export default function WhyVanta() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.08 }
    );

    const els = sectionRef?.current?.querySelectorAll('.reveal-hidden');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="why-vanta"
      ref={sectionRef}
      className="relative py-32 px-6"
      aria-labelledby="why-vanta-heading"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 100px, rgba(106,92,255,0.02) 100px, rgba(106,92,255,0.02) 101px)',
        }}
      />
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex flex-col items-center gap-6 mb-20">
          <div className="reveal-hidden text-center">
            <div className="section-label mb-5">Why Players Choose Us</div>
            <h2
              id="why-vanta-heading"
              className="font-heading text-5xl md:text-6xl font-900 text-white leading-tight"
              style={{ fontWeight: 900 }}
            >
              The{' '}
              <span className="gradient-text-subtle">VANTA Difference</span>
            </h2>
          </div>
          <p className="text-lg text-vanta-muted max-w-xl leading-relaxed reveal-hidden text-center">
            We built the server we always wanted to play on. No compromises.
          </p>
        </div>

        {/* 2-Column Asymmetric Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-20">
          {features?.map((feature, index) => (
            <div
              key={feature?.id}
              className="bento-card-alt p-10 reveal-hidden group flex items-start gap-6"
              style={{ transitionDelay: `${0.05 * (index + 1)}s` }}
            >
              <div 
                className="bento-glow" 
                aria-hidden="true" 
                style={{ 
                  top: '-30px', 
                  left: '-30px', 
                  background: feature?.color 
                }} 
              />
              
              {/* Icon - Left Side */}
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl"
                style={{ 
                  background: `linear-gradient(135deg, ${feature?.color}30, ${feature?.color}15)`, 
                  color: feature?.color,
                  border: `2px solid ${feature?.color}40`
                }}
                aria-hidden="true"
              >
                {feature?.icon}
              </div>
              
              {/* Content - Right Side */}
              <div className="flex-1">
                <h3 className="font-heading text-2xl font-800 text-white mb-3" style={{ fontWeight: 800 }}>
                  {feature?.title}
                </h3>
                <p className="text-vanta-muted text-base leading-relaxed mb-4">
                  {feature?.description}
                </p>
                
                {/* Badges for 100% Vanilla */}
                {feature?.badges && (
                  <div className="flex gap-2.5 flex-wrap">
                    {feature?.badges?.map((badge) => (
                      <span 
                        key={badge} 
                        className="stat-badge" 
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Single stat for Low Ping EU */}
                {feature?.stat && (
                  <div className="text-4xl font-heading font-900 gradient-text" style={{ fontWeight: 900 }}>
                    {feature?.stat}
                  </div>
                )}
                
                {/* Multiple stats for Active Admins */}
                {feature?.stats && (
                  <div className="flex gap-6 flex-wrap">
                    {feature?.stats?.map((stat) => (
                      <div key={stat?.label}>
                        <div 
                          className="text-xl font-heading font-800 text-white" 
                          style={{ fontWeight: 800, color: feature?.color }}
                        >
                          {stat?.value}
                        </div>
                        <div className="text-xs text-vanta-dim uppercase tracking-widest">
                          {stat?.label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 reveal-hidden" style={{ transitionDelay: '0.4s' }}>
          {stats?.map((stat) => (
            <div key={stat?.label} className="text-center p-6 rounded-2xl bg-[#0b0b0f] border border-white/5">
              <div className="text-5xl md:text-6xl font-heading font-900 gradient-text mb-3" style={{ fontWeight: 900 }}>
                {stat?.value}
              </div>
              <div className="text-sm text-vanta-muted font-semibold">{stat?.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}